import { Injectable } from '@angular/core';
import { Observable, from, map, catchError, of } from 'rxjs';
import { PMTiles, Protocol } from 'pmtiles';
import * as maplibregl from 'maplibre-gl';
import { GeoJSONFeatureCollection } from '../interfaces/simulation/ISimulation';
import { VectorTile } from '@mapbox/vector-tile';
import Pbf from 'pbf';

@Injectable({
  providedIn: 'root'
})
export class PmtilesConverterService {

  constructor() {
    // Register PMTiles protocol
    const protocol = new Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);
  }

  /**
   * Converts a PMTiles file to GeoJSON
   * @param pmtilesUrl - The URL of the PMTiles file
   * @returns Observable of GeoJSONFeatureCollection
   */
  convertPmtilesToGeoJSON(pmtilesUrl: string): Observable<GeoJSONFeatureCollection> {
    return from(this.extractFeaturesFromPmtiles(pmtilesUrl)).pipe(
      map(features => ({
        type: "FeatureCollection" as const,
        features: features
      })),
      catchError(error => {
        console.error('Error converting PMTiles to GeoJSON:', error);
        return of({
          type: "FeatureCollection" as const,
          features: []
        });
      })
    );
  }

  /**
   * Extracts features from PMTiles file
   * @param pmtilesUrl - The URL of the PMTiles file
   * @returns Promise of features array
   */
private async extractFeaturesFromPmtiles(pmtilesUrl: string): Promise<any[]> {
  const pmtiles = new PMTiles(pmtilesUrl);
  const metadata = await pmtiles.getMetadata();
  
  const z = 10; // scegli un livello di zoom appropriato
  const features: any[] = [];

  // ottieni l'elenco dei tile in base al livello di zoom
  for (let x = 0; x < Math.pow(2, z); x++) {
    for (let y = 0; y < Math.pow(2, z); y++) {
      try {
        const tileData = await pmtiles.getZxy(z, x, y);
        if (!tileData) continue;

        const tile = new VectorTile(new Pbf(tileData.data));
        Object.keys(tile.layers).forEach(layerName => {
          const layer = tile.layers[layerName];
          for (let i = 0; i < layer.length; i++) {
            const feat = layer.feature(i).toGeoJSON(x, y, z);
            features.push(feat);
          }
        });
      } catch (err) {
        // console.warn(`Tile ${z}/${x}/${y} non trovato`, err);
      }
    }
  }
  
  return features;
}

  /**
   * Gets bounding box from PMTiles file
   * @param pmtilesUrl - The URL of the PMTiles file
   * @returns Promise of bounding box [minLon, minLat, maxLon, maxLat]
   */
  async getBboxFromPmtiles(pmtilesUrl: string): Promise<[number, number, number, number]> {
    const pmtiles = new PMTiles(pmtilesUrl);
    const header = await pmtiles.getHeader();
    
    if (!header) {
      throw new Error('Could not read PMTiles header');
    }

    return [header.minLon, header.minLat, header.maxLon, header.maxLat];
  }
public async getSourceLayerName(pmtilesUrl: string): Promise<string> {
    const pmtiles = new PMTiles(pmtilesUrl);
    try {
      // 1. Assert the type of the metadata to our new interface
      const metadata = (await pmtiles.getMetadata()) as any;
      
      // 2. Now TypeScript knows the shape, and this check is safe
      if (metadata?.vector_layers?.length > 0) {
        return metadata.vector_layers[0].id;
      }
      
      throw new Error(`Nessun vector_layer trovato nei metadati di ${pmtilesUrl}`);

    } catch (error) {
      console.error("Errore durante la lettura dei metadati PMTiles:", error);
      throw error;
    }
  }
} 